import React, { FC, useState, useEffect, useRef, useMemo } from 'react'
import { Message, Conversation, Tag, ConfigOption } from '../hooks/useChat'
import TagManager from './TagManager'

type Props = {
    activeConversation: Conversation | null
    messages: Message[]
    loading: boolean
    onSendMessage: (contactId: number, content: string, channel: 'WHATSAPP' | 'SMS', configId?: number) => void
    onOpenTemplateDrawer?: (contactId: number) => void

    // Tag Props
    availableTags: Tag[]
    onCreateTag: (name: string, color: string) => Promise<any>
    onManageTag: (contactId: number, tagId: number, action: 'add' | 'remove') => Promise<any>

    // Channel Config
    configSource?: string
    availableConfigs?: ConfigOption[]
}

const ChatWindow: FC<Props> = ({
    activeConversation,
    messages,
    loading,
    onSendMessage,
    onOpenTemplateDrawer,
    availableTags,
    onCreateTag,
    onManageTag,
    configSource,
    availableConfigs = []
}) => {
    const [inputText, setInputText] = useState('')
    const [selectedChannel, setSelectedChannel] = useState<'WHATSAPP' | 'SMS'>('SMS')
    const [selectedConfigId, setSelectedConfigId] = useState<number | undefined>(undefined)

    // Auto-select config if only one available or match source
    useEffect(() => {
        if (availableConfigs.length > 0) {
            // If currently selected is not in available, reset
            if (selectedConfigId && !availableConfigs.find(c => c.id === selectedConfigId)) {
                setSelectedConfigId(undefined)
            }
        }
    }, [availableConfigs, selectedConfigId])
    const [filterWhatsApp, setFilterWhatsApp] = useState(true)
    const [filterSMS, setFilterSMS] = useState(true)
    const [showTagManager, setShowTagManager] = useState(false)
    const messagesEndRef = useRef<HTMLDivElement>(null)

    // Calculate if WhatsApp session is active (last INBOUND message < 24 hours)
    const whatsappSessionInfo = useMemo(() => {
        const now = new Date()
        const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000)

        // Find the most recent INBOUND message
        const inboundMessages = messages.filter(m => m.direction === 'INBOUND' && m.channel === 'WHATSAPP')
        if (inboundMessages.length === 0) {
            return { isActive: false, lastInbound: null, hoursRemaining: 0 }
        }

        const lastInbound = inboundMessages.reduce((latest, msg) => {
            const msgDate = new Date(msg.created_at)
            const latestDate = latest ? new Date(latest.created_at) : new Date(0)
            return msgDate > latestDate ? msg : latest
        }, null as Message | null)

        if (!lastInbound) {
            return { isActive: false, lastInbound: null, hoursRemaining: 0 }
        }

        const lastInboundDate = new Date(lastInbound.created_at)
        const isActive = lastInboundDate > twentyFourHoursAgo
        const sessionExpiry = new Date(lastInboundDate.getTime() + 24 * 60 * 60 * 1000)
        const hoursRemaining = isActive ? Math.round((sessionExpiry.getTime() - now.getTime()) / (60 * 60 * 1000)) : 0

        return { isActive, lastInbound, hoursRemaining }
    }, [messages])

    // Auto-select channel based on session status
    useEffect(() => {
        if (whatsappSessionInfo.isActive) {
            setSelectedChannel('WHATSAPP')
        } else {
            setSelectedChannel('SMS')
        }
    }, [whatsappSessionInfo.isActive, activeConversation?.id])

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }

    useEffect(() => {
        scrollToBottom()
    }, [messages, activeConversation])

    const handleSend = () => {
        if (!inputText.trim() || !activeConversation) return
        onSendMessage(activeConversation.id, inputText, selectedChannel, selectedConfigId)
        setInputText('')
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            handleSend()
        }
    }

    const handleOpenTemplate = () => {
        if (activeConversation && onOpenTemplateDrawer) {
            onOpenTemplateDrawer(activeConversation.id)
        }
    }

    if (!activeConversation) {
        return (
            <div className='card card-flush h-100 d-flex justify-content-center align-items-center'>
                <div className='text-center'>
                    <i className='bi bi-chat-square-text fs-1 text-muted mb-3 d-block' style={{ fontSize: '4rem' }}></i>
                    <span className='text-muted fs-4'>Select a conversation to start chatting</span>
                </div>
            </div>
        )
    }

    return (
        <div className='card card-flush h-100 chat-window'>
            <div className='card-header' id='kt_chat_messenger_header'>
                <div className='card-title'>
                    <div className='d-flex justify-content-center flex-column me-3'>
                        <a href='#' className='fs-4 fw-bolder text-gray-900 text-hover-primary me-1 mb-2 lh-1'>
                            {activeConversation.full_name}
                        </a>
                        <div className='mb-2 lh-1 text-muted fs-7'>
                            {activeConversation.phone}
                        </div>
                        <div className='mb-0 lh-1 d-flex align-items-center'>
                            {whatsappSessionInfo.isActive ? (
                                <>
                                    <span className='badge badge-success badge-circle w-10px h-10px me-1'></span>
                                    <span className='fs-7 fw-bold text-success'>
                                        WhatsApp active ({whatsappSessionInfo.hoursRemaining}h remaining)
                                    </span>
                                </>
                            ) : (
                                <>
                                    <span className='badge badge-secondary badge-circle w-10px h-10px me-1'></span>
                                    <span className='fs-7 fw-bold text-muted'>
                                        WhatsApp session expired
                                    </span>
                                </>
                            )}

                        </div>
                        {configSource && (
                            <div className='fs-9 text-muted mt-1 d-flex align-items-center'>
                                <span className="me-1">Via:</span>
                                {configSource.startsWith('organization:') ? (
                                    <>
                                        <i className="bi bi-building text-muted fs-9 me-1"></i>
                                        {configSource.replace('organization:', '')}
                                    </>
                                ) : configSource.startsWith('user:') ? (
                                    <>
                                        <i className="bi bi-person text-muted fs-9 me-1"></i>
                                        {configSource.replace('user:', '')}
                                    </>
                                ) : (
                                    configSource
                                )}
                            </div>
                        )}
                    </div>
                </div>

                <div className='card-toolbar d-flex align-items-center gap-3'>
                    <div className='d-flex align-items-center gap-3'>
                        <label className='form-check form-check-sm form-check-custom form-check-solid d-flex align-items-center gap-1 cursor-pointer mb-0'>
                            <input
                                className='form-check-input'
                                type='checkbox'
                                checked={filterWhatsApp}
                                onChange={(e) => setFilterWhatsApp(e.target.checked)}
                                style={{ borderColor: '#25D366' }}
                            />
                            <span className='form-check-label fs-7'>
                                <i className='bi bi-whatsapp text-success me-1'></i>WhatsApp
                            </span>
                        </label>
                        <label className='form-check form-check-sm form-check-custom form-check-solid d-flex align-items-center gap-1 cursor-pointer mb-0'>
                            <input
                                className='form-check-input'
                                type='checkbox'
                                checked={filterSMS}
                                onChange={(e) => setFilterSMS(e.target.checked)}
                            />
                            <span className='form-check-label fs-7'>
                                <i className='bi bi-chat-dots text-muted me-1'></i>SMS
                            </span>
                        </label>
                    </div>
                    {!whatsappSessionInfo.isActive && onOpenTemplateDrawer && (
                        <button
                            className='btn btn-sm btn-light-success'
                            onClick={handleOpenTemplate}
                            title='Send WhatsApp Template to reopen conversation'
                        >
                            <i className='bi bi-whatsapp me-1'></i>
                            Send Template
                        </button>
                    )}

                    <button
                        className='btn btn-sm btn-light btn-active-light-primary ms-2'
                        onClick={() => setShowTagManager(true)}
                        title="Tags Manager"
                    >
                        <i className='bi bi-tags fs-2'></i>
                    </button>
                </div>
            </div>

            <div className='card-body d-flex flex-column' id='kt_chat_messenger_body'>
                <div
                    className='scroll-y me-n5 pe-5 flex-grow-1'
                    data-kt-element='messages'
                    data-kt-scroll='true'
                    data-kt-scroll-activate='{default: false, lg: true}'
                    data-kt-scroll-max-height='auto'
                    data-kt-scroll-dependencies='#kt_header, #kt_app_header, #kt_toolbar, #kt_app_toolbar, #kt_footer, #kt_app_footer, #kt_chat_messenger_header, #kt_chat_messenger_footer'
                    data-kt-scroll-wrappers='#kt_content, #kt_app_content, #kt_chat_messenger_body'
                    data-kt-scroll-offset='5px'
                    style={{ overflowY: 'auto' }}
                >
                    {loading && (
                        <div className="d-flex justify-content-center my-4">
                            <span className="spinner-border text-primary" role="status"></span>
                        </div>
                    )}

                    {messages.filter(msg => {
                        if (msg.channel === 'WHATSAPP' && !filterWhatsApp) return false
                        if (msg.channel === 'SMS' && !filterSMS) return false
                        return true
                    }).map((msg) => (
                        <div
                            key={msg.id || Math.random()}
                            className={`d-flex justify-content-${msg.direction === 'OUTBOUND' ? 'end' : 'start'} mb-10`}
                        >
                            <div
                                className={`d-flex flex-column align-items-${msg.direction === 'OUTBOUND' ? 'end' : 'start'}`}
                            >
                                <div className='d-flex align-items-center mb-2'>
                                    {msg.direction === 'INBOUND' && (
                                        <div className='symbol symbol-35px symbol-circle'>
                                            <span className='symbol-label bg-light-danger text-danger fs-6 fw-bolder'>
                                                {activeConversation.full_name.charAt(0)}
                                            </span>
                                        </div>
                                    )}
                                    <div className='ms-3'>
                                        <span className='text-muted fs-7 mb-1'>
                                            {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                    </div>
                                </div>

                                <div
                                    className={`p-5 rounded ${msg.channel === 'WHATSAPP'
                                        ? msg.direction === 'OUTBOUND'
                                            ? 'bg-light-success text-dark'
                                            : 'bg-success bg-opacity-10 text-dark'
                                        : msg.direction === 'OUTBOUND'
                                            ? 'bg-light text-dark'
                                            : 'bg-secondary bg-opacity-10 text-dark'
                                        } fw-bold mw-lg-400px text-start`}
                                    data-kt-element='message-text'
                                    style={{ whiteSpace: 'pre-wrap' }}
                                >
                                    {msg.content}
                                </div>

                                <div className={`d-flex align-items-center mt-1 ${msg.direction === 'OUTBOUND' ? 'justify-content-end' : 'justify-content-start'}`}>
                                    {msg.channel === 'WHATSAPP' ? (
                                        <span className="badge badge-light-success fs-8 d-flex align-items-center">
                                            <i className="bi bi-whatsapp text-success me-1" style={{ fontSize: '0.75rem' }}></i>
                                            WhatsApp
                                        </span>
                                    ) : (
                                        <span className="badge badge-light fs-8 d-flex align-items-center">
                                            <i className="bi bi-chat-dots text-muted me-1" style={{ fontSize: '0.75rem' }}></i>
                                            SMS
                                        </span>
                                    )}
                                    {msg.direction === 'OUTBOUND' && (
                                        <span className="ms-2">
                                            {msg.status === 'READ' ? <i className="bi bi-check2-all text-primary"></i> : <i className="bi bi-check2 text-muted"></i>}
                                        </span>
                                    )}
                                </div>

                            </div>
                        </div>
                    ))}

                    {messages.length > 0 && !messages.some(msg =>
                        (msg.channel === 'WHATSAPP' && filterWhatsApp) || (msg.channel === 'SMS' && filterSMS)
                    ) && (
                            <div className='text-center text-muted py-5'>
                                <i className='bi bi-funnel fs-1 d-block mb-2'></i>
                                <span>No messages matching selected filters</span>
                            </div>
                        )}
                    <div ref={messagesEndRef} />
                </div>
            </div>

            <div className='card-footer pt-4  position-relative' id='kt_chat_messenger_footer'>
                {!whatsappSessionInfo.isActive && (
                    <div className='alert alert-warning d-flex align-items-center py-2 px-3 mb-3'>
                        <i className='bi bi-exclamation-triangle-fill text-warning me-2'></i>
                        <small>WhatsApp session expired. Use SMS or send a Template to reopen.</small>
                    </div>
                )}

                <textarea
                    className='form-control form-control-flush mb-3'
                    rows={1}
                    data-kt-element='input'
                    placeholder='Type a message'
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    onKeyDown={handleKeyDown}
                ></textarea>

                <div className='d-flex flex-stack'>
                    <div className='d-flex align-items-center me-2'>
                        <button
                            className='btn btn-sm btn-icon btn-active-light-primary me-1'
                            type='button'
                            data-bs-toggle='tooltip'
                            title='Coming soon'
                        >
                            <i className='bi bi-paperclip fs-3'></i>
                        </button>
                    </div>

                    <button
                        className='btn btn-sm btn-light btn-active-light-primary'
                        onClick={() => setShowTagManager(true)}
                        title="Manage Tags"
                    >
                        <i className='bi bi-tags fs-2'></i>
                    </button>

                    {onOpenTemplateDrawer && (
                        <button className='btn btn-sm btn-icon btn-active-light-primary' onClick={handleOpenTemplate} title="Templates">
                            <i className='bi bi-file-text fs-2'></i>
                        </button>
                    )}
                    <div className='d-flex align-items-center'>
                        {/* Channel Config Selector */}
                        {availableConfigs.length > 0 && (
                            <div className="me-2" style={{ width: '150px' }}>
                                <select
                                    className="form-select form-select-sm"
                                    value={selectedConfigId || ''}
                                    onChange={(e) => setSelectedConfigId(e.target.value ? Number(e.target.value) : undefined)}
                                    title="Send using..."
                                >
                                    <option value="">Default Channel</option>
                                    {availableConfigs.map(config => (
                                        <option key={config.id} value={config.id}>
                                            {config.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                        <select
                            className='form-select form-select-sm me-2'
                            style={{ width: '140px' }}
                            value={selectedChannel}
                            onChange={(e) => setSelectedChannel(e.target.value as 'WHATSAPP' | 'SMS')}
                        >
                            <option
                                value='WHATSAPP'
                                disabled={!whatsappSessionInfo.isActive}
                            >
                                📱 WhatsApp {!whatsappSessionInfo.isActive && '(expired)'}
                            </option>
                            <option value='SMS'>
                                💬 SMS
                            </option>
                        </select>
                        <button
                            className={`btn ${selectedChannel === 'WHATSAPP' ? 'btn-success' : 'btn-secondary'}`}
                            type='button'
                            data-kt-element='send'
                            onClick={handleSend}
                            disabled={!inputText.trim()}
                        >
                            Send
                        </button>
                    </div>
                </div>
            </div>
            {/* Template Drawer or other modals could be here */}
            {activeConversation && (
                <TagManager
                    show={showTagManager}
                    onHide={() => setShowTagManager(false)}
                    contactId={activeConversation.id}
                    currentTags={activeConversation.tags || []}
                    availableTags={availableTags}
                    onCreateTag={onCreateTag}
                    onManageTag={onManageTag}
                />
            )}
        </div >
    )
}

export { ChatWindow }
